'use client'

import { useProductExtraFeatures } from '@/hooks/react-query/products/use-product'
import { sanitize } from '@/lib/helpers/sanitize'
import { cn } from '@/lib/utils'
import { ExtraFeature } from '@/types/product-detail'
import { useTranslations } from 'next-intl'
import Link from 'next/link'

type Props = {
  className?: string
  shortDescription?: string
  supportMovement?: string
  productId: string
  productSPU?: string
}

export const ProductDescriptionSpecifications = ({
  className,
  shortDescription,
  supportMovement,
  productId,
  productSPU,
}: Props) => {
  const { data: features } = useProductExtraFeatures(productId)
  return (
    <div className={className}>
      <h3 className='mb-2 font-sans text-base font-normal leading-5 text-neutral-600 lg:hidden'>
        {shortDescription}
      </h3>
      <FabricBar supportMovement={supportMovement} />
      <Attributes features={features || []} productSPU={productSPU} />
    </div>
  )
}

const FabricBar = ({ supportMovement }: { supportMovement?: string }) => {
  const t = useTranslations('product_detail.title')

  const configLevels = [
    {
      label: t('light'),
      value: 'light',
    },
    {
      label: t('medium'),
      value: 'medium',
    },
    {
      label: t('strong'),
      value: 'strong',
    },
  ]

  if (!supportMovement) return null

  return (
    <div className=''>
      <p className='mb-1 font-sans text-sm leading-4.5 text-neutral-600 lg:mb-1.5'>
        {t('support_movement')}:
      </p>
      <ul
        className='flex items-center gap-0.5'
        role='list'
        aria-label={t('support_movement_level')}
      >
        {configLevels.map((item, idx) => (
          <li
            className='flex-1'
            key={item.value}
            aria-label={`${t('support_movement_level')} ${item.label}`}
          >
            <span
              className={cn(
                'inline-block h-2 w-full rounded-full bg-neutral-200 p-0.5',
                supportMovement &&
                  item.value === supportMovement &&
                  'bg-neutral-900'
              )}
            >
              {(idx === 0 || idx === configLevels.length - 1) && (
                <span
                  className={cn(
                    'block h-1 w-1 rounded-full bg-light',
                    idx === configLevels.length - 1 && 'ml-auto bg-neutral-400'
                  )}
                ></span>
              )}
            </span>
            <p
              className={cn(
                'mt-1.5 pl-0.5 font-sans text-xs font-medium uppercase leading-4.5 text-neutral-900 lg:text-base lg:leading-5',

                idx > 0 && 'text-center',
                idx === configLevels.length - 1 && 'text-end'
              )}
            >
              {item.label}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

const Attributes = ({
  features,
  productSPU,
}: {
  features?: ExtraFeature[]
  productSPU?: string
}) => {
  const t = useTranslations('product_detail.title')

  if (!features || features.length === 0) {
    return null
  }

  // Tạo một bản sao của mảng features để tránh mutate props
  const updatedFeatures = [...features]

  if (features.length > 0 && productSPU) {
    // Kiểm tra xem mã sản phẩm đã tồn tại chưa
    const productFeatureExists = updatedFeatures.some(
      (feature) =>
        feature.title === 'Mã sản phẩm' &&
        feature.values?.[0]?.title === productSPU
    )

    if (!productFeatureExists) {
      const featureTech = updatedFeatures.find(
        (feature) => feature.title === 'Công nghệ'
      )

      const productFeature = {
        title: t('product_code'),
        values: [{ title: productSPU, link: '' }],
        is_selected: true,
      }

      if (featureTech) {
        // Chèn vào vị trí thứ 2 (index = 1)
        updatedFeatures.splice(1, 0, productFeature)
      } else {
        // Nếu không có "Công nghệ", chèn vào đầu
        updatedFeatures.unshift(productFeature)
      }
    }
  }

  return (
    <ul className='mt-5 py-2' role='list' aria-label={t('specifications')}>
      {updatedFeatures.map((feature, index) => (
        <li
          key={feature.title || `feature-${index}`}
          className='mb-2 flex items-start gap-3.5 border-b border-neutral-200 pb-3 last-of-type:mb-0'
        >
          <h4
            className='min-w-24 font-sans text-xs uppercase text-neutral-500 lg:min-w-[120px]'
            dangerouslySetInnerHTML={{ __html: sanitize(feature.title) || '' }}
          ></h4>
          <div>
            {feature?.values?.map((item) => {
              return item.link ? (
                <Link
                  key={item.title || Math.random().toString()}
                  href={item.link}
                  className='line-clamp-3 font-sans text-sm text-primary underline hover:text-primary-600 hover:underline'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {item.title || ''}
                </Link>
              ) : (
                <p
                  key={item.title || Math.random().toString()}
                  className='line-clamp-3 font-sans text-sm text-neutral-600 md:text-sm 2xl:text-base'
                >
                  {item.title || ''}
                </p>
              )
            })}
          </div>
        </li>
      ))}
      <li className='font-pangea font-criteria font-medium text-neutral-600'>
        * Proudly Made In Vietnam
      </li>
    </ul>
  )
}
